import { useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../../services/api';
import type { QuickReply } from '../../types';

type Props = {
  draft: string;
  onSelect: (reply: QuickReply) => void;
};

const QuickReplyAutocomplete = ({ draft, onSelect }: Props) => {
  const [replies, setReplies] = useState<QuickReply[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const loadedRef = useRef(false);
  const lastQuery = useRef<string | null>(null);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void api.quickReplies().then(setReplies).catch(() => undefined);
  }, []);

  const query = useMemo(() => {
    const match = draft.match(/\/(\w*)$/);
    return match?.[1] ? match[1].toLowerCase() : null;
  }, [draft]);

  const matches = useMemo(() => {
    if (query === null) return [];
    return replies
      .filter((reply) => reply.shortcut.toLowerCase().includes(query))
      .slice(0, 5);
  }, [replies, query]);

  if (lastQuery.current !== query) {
    lastQuery.current = query;
    if (selectedIndex !== 0) setSelectedIndex(0);
  }

  if (query === null || matches.length === 0) return null;

  return (
    <div className="quick-reply-autocomplete" role="listbox" aria-label="Quick replies">
      {matches.map((reply, index) => (
        <button
          key={reply.id}
          type="button"
          role="option"
          aria-selected={index === selectedIndex}
          className={`qr-suggestion ${index === selectedIndex ? 'selected' : ''}`}
          onClick={() => onSelect(reply)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <code>/{reply.shortcut}</code>
          <span className="qr-preview">{reply.content.slice(0, 60)}</span>
        </button>
      ))}
    </div>
  );
};

export default QuickReplyAutocomplete;
